import { useState, useEffect } from 'react';
import { api } from '../../api';

// `toAdmin` (#208, #210): la empresa de un reseller, que delega directo al administrador de la plataforma.
export function DelegationPanel({ clientId, toAdmin = false }) {
  const who = toAdmin ? 'al administrador de la plataforma' : 'a tu proveedor';
  const [delegations, setDelegations] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [form, setForm]               = useState({
    op_explore: false, op_restore: false, op_view_key: false, op_recovery_kit: false,
  });
  const [confirmModal, setConfirmModal] = useState(false);
  const [pendingSubs, setPendingSubs]   = useState([]);
  const [approvingId, setApprovingId]   = useState(null);

  const load = () => {
    api.getClientDelegations(clientId).then(list => {
      setDelegations(list);
      // Find the main (non-sub) active delegation
      const main = list.find(d => !d.parent_delegation_id && d.status === 'active');
      if (main) {
        setForm({
          op_explore:      main.op_explore,
          op_restore:      main.op_restore,
          op_view_key:     main.op_view_key,
          op_recovery_kit: main.op_recovery_kit,
        });
      }
      // Find pending sub-delegations
      setPendingSubs(list.filter(d => d.status === 'pending_client_approval'));
    }).catch(() => setDelegations([]));
  };

  useEffect(() => { load(); }, [clientId]);  // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveClientDelegation(clientId, form);
      load();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
      setConfirmModal(false);
    }
  };

  const handleApprove = async (id, approved) => {
    setApprovingId(id);
    try {
      await api.approveSubdelegation(id, approved);
      load();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setApprovingId(null);
    }
  };

  if (delegations === null) return <div style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'var(--mono)' }}>Cargando delegaciones...</div>;

  const ops = [
    { key: 'op_explore',      label: 'Explorar snapshots',           desc: 'Permite ver y navegar los archivos de backups' },
    { key: 'op_restore',      label: 'Restaurar datos',              desc: 'Permite solicitar y ejecutar restauraciones' },
    { key: 'op_view_key',     label: 'Ver clave de repositorio',     desc: 'Permite acceder a las credenciales del repositorio' },
    { key: 'op_recovery_kit', label: 'Descargar kit de recuperación',desc: 'Permite descargar el kit completo de recuperación' },
  ];

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: 20, marginBottom: 20 }}>
      <div style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 16 }}>
        Delegación de Operaciones
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
        Autorizá {who} a realizar operaciones sobre {toAdmin ? 'los backups de tu empresa' : 'tus backups'}. Por defecto, todas las operaciones están bloqueadas.
      </div>

      {pendingSubs.length > 0 && (
        <div style={{ background: 'rgba(255,200,0,0.08)', border: '1px solid var(--yellow)', padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--yellow)', marginBottom: 10 }}>
            Solicitudes de sub-delegación pendientes:
          </div>
          {pendingSubs.map(sub => (
            <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 12 }}>
                Tu proveedor solicita delegar al administrador: {[
                  sub.op_explore && 'Explorar',
                  sub.op_restore && 'Restaurar',
                  sub.op_view_key && 'Ver clave',
                  sub.op_recovery_kit && 'Kit de recuperación',
                ].filter(Boolean).join(', ')}
              </span>
              <button onClick={() => handleApprove(sub.id, true)} disabled={approvingId === sub.id} style={{ background: 'var(--green)', border: 'none', color: 'var(--bg)', padding: '4px 10px', fontSize: 11, cursor: approvingId === sub.id ? 'not-allowed' : 'pointer', fontFamily: 'var(--mono)', opacity: approvingId === sub.id ? 0.5 : 1 }}>{approvingId === sub.id ? '...' : 'APROBAR'}</button>
              <button onClick={() => handleApprove(sub.id, false)} disabled={approvingId === sub.id} style={{ background: 'transparent', border: '1px solid var(--red)', color: 'var(--red)', padding: '4px 10px', fontSize: 11, cursor: approvingId === sub.id ? 'not-allowed' : 'pointer', fontFamily: 'var(--mono)', opacity: approvingId === sub.id ? 0.5 : 1 }}>{approvingId === sub.id ? '...' : 'RECHAZAR'}</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
        {ops.map(op => (
          <label key={op.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '10px 12px', border: '1px solid var(--border)', background: form[op.key] ? 'rgba(0,200,100,0.05)' : 'transparent' }}>
            <input
              type="checkbox"
              checked={form[op.key]}
              onChange={e => setForm(f => ({ ...f, [op.key]: e.target.checked }))}
              style={{ marginTop: 2 }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{op.label}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{op.desc}</div>
            </div>
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => setConfirmModal(true)}
          disabled={saving}
          style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}
        >
          {saving ? 'GUARDANDO...' : 'GUARDAR PERMISOS'}
        </button>
      </div>

      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: 460, padding: 24 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 14, marginBottom: 16 }}>Confirmar cambios de delegación</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              Vas a autorizar {who} a realizar las siguientes operaciones:
            </div>
            <ul style={{ fontSize: 13, marginBottom: 16, paddingLeft: 20 }}>
              {ops.filter(op => form[op.key]).map(op => <li key={op.key}>{op.label}</li>)}
              {ops.filter(op => form[op.key]).length === 0 && <li style={{ color: 'var(--muted)' }}>Ninguna (se revocarán todos los permisos)</li>}
            </ul>
            {!toAdmin && (
              <div style={{ background: 'rgba(255,200,0,0.08)', border: '1px solid var(--yellow)', padding: '10px 14px', fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--yellow)', marginBottom: 16 }}>
                Los permisos deshabilitados revocan automáticamente cualquier sub-delegación activa.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
              <button onClick={handleSave} disabled={saving} style={{ background: 'var(--accent)', border: '1px solid var(--accent)', color: 'var(--bg)', padding: '7px 16px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer', fontWeight: 700 }}>CONFIRMAR</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
